import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { CurrencyDollar, CircleNotch } from '@phosphor-icons/react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Currency Normalization Card.
 * One-click button that aligns every contact whose currency differs from the
 * company's configured currency, sweeping up legacy USD-defaulted records
 * created before the platform default was changed to the tenant's currency.
 */
export default function CurrencyNormalizationCard({ company, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleNormalize = async () => {
    if (!canEdit) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/company/normalize-contact-currencies`);
      setLastResult(res.data);
      const { matched, modified, target_currency } = res.data || {};
      if (modified > 0) {
        toast.success(`Updated ${modified} contact(s) to ${target_currency}`);
      } else {
        toast.info(`Already aligned — every contact uses ${target_currency}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to normalize currencies');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border rounded-2xl p-6 space-y-4" data-testid="currency-normalization-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 flex items-center justify-center">
          <CurrencyDollar size={20} weight="duotone" />
        </div>
        <div>
          <h3 className="font-heading font-semibold">Contact Currencies</h3>
          <p className="text-xs text-muted-foreground">
            Re-align all customer & supplier contacts to your company currency
            <span className="ml-1 font-medium">({company?.currency || 'AED'})</span>.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        New contacts already inherit your company currency automatically. Use this
        only if you have legacy contacts that were created when the platform
        default was USD.
      </div>

      {lastResult && (
        <div className="rounded-lg border bg-emerald-500/5 border-emerald-500/30 px-3 py-2 text-xs">
          Last run — matched <strong>{lastResult.matched}</strong>, modified{' '}
          <strong>{lastResult.modified}</strong>, target{' '}
          <strong>{lastResult.target_currency}</strong>.
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleNormalize}
          disabled={!canEdit || loading}
          data-testid="normalize-currencies-btn"
        >
          {loading ? <CircleNotch className="mr-2 animate-spin" size={16} /> : <CurrencyDollar className="mr-2" size={16} />}
          Normalize Contact Currencies
        </Button>
      </div>
    </div>
  );
}
